import { List as ImmutableList, is as ImmutableIs } from 'immutable';
import AltInstance from '../Globals/Scripts/AltInstance';
import NavigationActions from '../Actions/NavigationActions'
import Configuration from '../Globals/Scripts/Configuration';
import Browser from '../Utilities/Browser';

function calculateHierarchicalMenuItemDistances(menu, newRoute) {
    let hierarchicalMenuItemDistances = {};

    ImmutableList(menu.keys()).forEach(menuItemKey => {
        const menuItem = menu.get(menuItemKey);
        if (typeof menuItem === 'string') {
            // This is a link, see how far the href matches.
            if (newRoute.toLowerCase().startsWith(menuItem.toLowerCase())) {
                hierarchicalMenuItemDistances[menuItemKey] = newRoute.length - menuItem.length;
            }
        } else {
            let subMenuHierarchicalDistances = calculateHierarchicalMenuItemDistances(menuItem, newRoute);
            if (Object.keys(subMenuHierarchicalDistances).length) {
                hierarchicalMenuItemDistances[menuItemKey] = subMenuHierarchicalDistances;
            }
        }
    });

    return hierarchicalMenuItemDistances;
}

function findNearestMenuPath(hierarchy) {
    let nearestDistance = Number.MAX_VALUE;
    let path = [];

    const iterate = (h, pathSoFar) => {
        Object.keys(h).forEach(item => {
            let pathItem = h[item];
            let currentPath = pathSoFar.concat([item]);
            if (typeof pathItem === 'object') {
                iterate(pathItem, currentPath);
            } else {
                if (nearestDistance > pathItem) {
                    path = currentPath;
                    nearestDistance = pathItem;
                }
            }
        });
    }

    iterate(hierarchy, []);

    return path;
}

export class NavigationStore {
    constructor() {
        this.bindActions(NavigationActions);
        this.menu = ImmutableList();
        this.pendingRouteChange = null;
        this.currentMenuSelection = null;
        this.currentPageTitle = null;
        this.currentPageHelpLink = null;
        this.currentBreadcrumbs = [];
        this.isEntityModified = false;
        this.originalEntity = null;
        this.modifiedEntity = null;
    }

    onMenuLoaded(menu) {
        this.menu = menu;
        if (this.pendingRouteChange) {
            this.onRouteChanged(this.pendingRouteChange);
        }
    }

    onRouteChanged(newRoute) {
        // Can't use waitFor here because it's waiting on itself. Not cool for Alt.
        this.pendingRouteChange = newRoute;

        if (this.menu && this.menu.count()) {
            let pathname = this.pendingRouteChange.pathname;

            // If the route's pathname starts with "/App/Consignments" then we need to do some workarounds so that
            // the pathname can match a menu item's path which is defined in AvailableFeaturesProvider.
            // This is so the correct menu and menu items are highlighted with the "selected" style.
            // This only impacts App pages because Online do not highlight menus and menu items.
            if(this.pendingRouteChange && this.pendingRouteChange.pathname.startsWith("/App/Consignments")) {
                if(this.pendingRouteChange.pathname === "/App/Consignments/Dashboard") {
                    // If the Dashboard page is rendered then the search parameters would need to be examined because
                    // each menu item is a different tab on the same page.
                    // The "pathname" variable must match the below for the highlight to apply:
                    // - Summary menu item link: /App/Consignments/Dashboard?tab=summary
                    // - Dashboard menu item link: /App/Consignments/Dashboard?tab=dashboard
                    if(this.pendingRouteChange.search.startsWith("?tab=dashboard")) {
                        pathname = "/App/Consignments/Dashboard?tab=dashboard";
                    } else {
                        pathname = "/App/Consignments/Dashboard?tab=summary";
                    }
                }
            }

            const hierarchicalMenuItemDistances = calculateHierarchicalMenuItemDistances(this.menu, pathname);
            const nearestMenuPath = findNearestMenuPath(hierarchicalMenuItemDistances);
            this.pendingRouteChange = null;
            this.currentMenuSelection = nearestMenuPath;
        }
    }

    onSetPageTitle(newPageTitle) {
        this.currentPageTitle = newPageTitle;
        Browser.document.title = Configuration.pageTitlePrefix + Configuration.pageTitleSeparator + newPageTitle;
    }

    onSetPageHelpLink(newPageHelpLink) {
        this.currentPageHelpLink = newPageHelpLink;
    }

    onSetBreadcrumbs(breadcrumbs) {
        this.currentBreadcrumbs = breadcrumbs;
    }

    onOriginalEntityReady(entity) {
        this.originalEntity = entity;
        this.modifiedEntity = null;
        this.isEntityModified = false;
    }

    onEntityModified(entity) {
        this.modifiedEntity = entity;
        this.isEntityModified = !ImmutableIs(this.originalEntity, this.modifiedEntity);
    }

    onNavigatedAway() {
        this.originalEntity = null;
        this.modifiedEntity = null;
        this.isEntityModified = false;
    }
}

export default AltInstance.createStore(NavigationStore, 'NavigationStore');
